import React, { createContext, useContext, useEffect, useState } from "react";
import { useCatalogApi } from "@/hooks/useCatalogApi";
import { useOrdersApiService } from "@/hooks/useOrdersApi";
import type { Location } from "@/types/order";
import { DELIVERY_PRODUCT_NAME } from "@/lib/orderUtils";

export interface Product {
  id: number;
  name: string;
  base_price: string;
  vat_rate?: string;
  custom?: boolean;
  category?: { id: number; name: string } | null;
  images?: {
    id: number;
    image: { presigned_url?: string; thumbnail_presigned_url?: string };
  }[];
  is_available_at_location?: boolean;
}

export interface Category {
  id: number;
  name: string;
  parent?: number | null;
}

interface CatalogContextType {
  loadCatalog: () => Promise<void>;
  products: Product[];
  categories: Category[];
  locations: Location[];
  selectedCategoryId: number | null;
  setSelectedCategoryId: React.Dispatch<React.SetStateAction<number | null>>;
  livraisonProduct?: Product | null;
  selectedCategoryIdDefault: number | null;
}

const CatalogContext = createContext<CatalogContextType | undefined>(undefined);

export const CatalogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { fetchProducts, fetchCategories } = useCatalogApi();
  const ordersService = useOrdersApiService();

  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [livraisonProduct, setLivraisonProduct] = useState<Product | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [selectedCategoryIdDefault, setSelectedCategoryIdDefault] = useState<number | null>(null);

  const loadCatalog = async () => {
    try {
      const [prods, cats, locs] = await Promise.all([
        fetchProducts(),
        fetchCategories(),
        ordersService.fetchLocations(),
      ]);
      
      setProducts(prods);
      setCategories(cats);
      setLocations(locs);

      // Find the "Livraison" product
      const livraison = prods.find((p: Product) => p.name === DELIVERY_PRODUCT_NAME);
      if (livraison) {
        setLivraisonProduct(livraison);
      }      

      // Auto-select "Bouquets" category if none selected
      const bouquetsCategory = cats.find(
        (cat: Category) => !cat.parent && cat.name.toLowerCase() === "bouquets",
      );
      
      if (bouquetsCategory) {
        setSelectedCategoryId((prev) => prev || bouquetsCategory.id);
        setSelectedCategoryIdDefault(bouquetsCategory.id);
      } else {
        // Fallback to first top-level category if "Bouquets" not found
        const topLevelCategories = cats.filter((cat: Category) => !cat.parent);
        if (topLevelCategories.length > 0) {
          setSelectedCategoryId((prev) => prev || topLevelCategories[0].id);
        }
      }
    } catch (error) {
      console.error("Failed to load catalog:", error);
    }
  };

  useEffect(() => {
    loadCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CatalogContext.Provider value={{ 
      loadCatalog, 
      products, 
      categories, 
      locations, 
      selectedCategoryId, 
      setSelectedCategoryId, 
      livraisonProduct,
      selectedCategoryIdDefault
    }}>
      {children}
    </CatalogContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useCatalog = (): CatalogContextType => {
  const context = useContext(CatalogContext);
  if (!context) {
    throw new Error("useCatalog must be used within a CatalogProvider");
  }
  return context;
};
